/* eslint-disable max-lines */
/* eslint-disable react/boolean-prop-naming */
/* eslint-disable react/jsx-handler-names */
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import Typography from "../../atoms/typography";
import { getSupportedFileType } from "../../../utils/pdp/personalization";
import { CAKE_TYPE, constants, toastStyles } from "../../../constants/pageConstants/pdpConstants";
import Toast from "../../atoms/toast";
import { setSuccessfullyAddedToast } from "../../../store/slices/personalizationSlice";
import CustomizedImagePersonalization from "../../molecules/pdpPersonalizations/customizedImagePersonalization";
import { cleverTapCommonEvents, cleverTapCommonWords, validationFor } from "../../../analytics/clevertapConstants";
import { COUNTRIES } from "../../../constants/common/common";
import { setPersonalizationErrorFromAddToCart } from "../../../store/slices/pdpSlice";
import ErrorMessage from "./errorMessage.jsx";
import { calDiscountPercentage, getProductDeliveryType, pageName } from "../../../utils/common";
import useAnalytics from "../../../analytics/useAnalytics";

const SingleImagePersonalization = dynamic(
  () => import("../../molecules/pdpPersonalizations/singleImagePersonalization"),
);
const MultiImagePersonalization = dynamic(
  () => import("../../molecules/pdpPersonalizations/multiImagePersonalization"),
);

/**
 * ImagePersonalization component provides an interface for uploading personalized images.
 * It renders an image preview, file size, type information, and manages errors related to invalid file sizes.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.item - Contains allowed file types, size limits, and other image details.
 * @param {Object} props.productId - Contains product id of the product.
 * @returns {JSX.Element} The personalized image upload component.
 */
const ImagePersonalization = ({
  item,
  productId,
  urlIdentifier,
  isCustomizedImage,
  isMobile,
  imageWithText,
  personalizedOptions,
  currentItem,
}) => {
  const { minSize, maxSize, minUnit, maxUnit, allowedMediaType, contentType } = item;
  const dispatch = useDispatch();
  const pathname = usePathname();
  const { trackEvent } = useAnalytics();

  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");
  const [previewImage, setPreviewImage] = useState(null);
  const [textEntered, setTextEntered] = useState(false);
  const [imageSizeError, setImageSizeError] = useState(false);

  const personalizationError = useSelector((state) => state.pdp.personalizationError);
  const selectedPincode = useSelector((state) => state.locationLock.selectedDomesticLocation?.pincode);
  const successfullyAddedToast = useSelector((state) => state.personalization.successfullyAddedToast);

  const {
    nextAvailableDifference,
    deliveryDates,
    deliveryDetailsData,
    address,
    selectedIntLocation,
    clientVariantType,
    catalogId,
  } = useSelector(
    (state) => ({
      deliveryDetails: state?.shippingDateAndSlotSelection?.deliveryDates,
      nextAvailableDifference: state?.shippingDateAndSlotSelection?.nextAvailableDifference,
      deliveryDates: state?.shippingDateAndSlotSelection?.deliveryDates,
      deliveryDetailsData: state?.shippingDateAndSlotSelection?.selectedDeliveryDetails,
      address: state?.locationLock?.selectedDomesticLocation?.address,
      selectedIntLocation: state?.locationLock.selectedIntLocation,
      clientVariantType: state?.pdp?.clientVariantType,
      catalogId: state?.appConfig?.catalogId,
    }),
    shallowEqual,
  );

  const discountPercentage = calDiscountPercentage(
    currentItem?.productPrice?.listPrice,
    currentItem?.productPrice?.price,
  );
  const productDeliveryType = getProductDeliveryType(currentItem?.primaryCategoryId);
  const cleverTapData = {
    page_type: cleverTapCommonWords.PDP,
    page_name: urlIdentifier,
    product_name: currentItem?.productName,
    pid: productId,
    product_category: currentItem?.primaryCategoryId,
    product_price: currentItem?.productPrice?.price,
    is_variant_available: currentItem?.isVariant === "Y",
    is_egg: clientVariantType?.toUpperCase() === CAKE_TYPE.WITH_EGG ?? false,
    delivery_type: productDeliveryType,
    is_international: currentItem?.catalogId !== COUNTRIES.INDIA,
    city: currentItem?.catalogId !== COUNTRIES.INDIA ? selectedIntLocation?.location : "",
    area: currentItem?.catalogId !== COUNTRIES.INDIA ? "" : address,
    pincode: currentItem?.catalogId !== COUNTRIES.INDIA ? "" : selectedPincode,
    system_pincode: currentItem?.catalogId !== COUNTRIES.INDIA ? "" : selectedPincode,
    country: currentItem?.catalogId,
    strike_price: currentItem?.productPrice?.listPrice,
    discount_percentage: discountPercentage,
    product_rating: currentItem?.productReviewsAndRatings?.ratingValue,
    product_review_count: currentItem?.productReviewsAndRatings?.totalReview,
    next_available_date_difference:
      currentItem?.catalogId !== COUNTRIES.INDIA ? "" : nextAvailableDifference?.nextAvailableDateDiff,
    date_of_delivery: deliveryDetailsData?.selectedDate,
    delivery_shipping: deliveryDetailsData?.currentShippingMethod?.shippingMethodName || "",
    delivery_shipping_fee: deliveryDetailsData?.timeSlots?.timeSlots?.shippingPrice ?? 0,
    delivery_slot: deliveryDetailsData?.timeSlots?.formatTime || "",
    earliest_delivery_date: deliveryDates?.deliveryDates?.[0] ?? "",
  };
  const clearPersonalizedData = useSelector((state) => state.personalization.clearPersonalizedData);

  useEffect(() => {
    if (clearPersonalizedData) {
      setImageSizeError(false);
      setTextEntered(false);
      setPreviewImage(null);
    }
  }, [clearPersonalizedData]);

  useEffect(() => {
    const isValid = imageWithText ? textEntered && previewImage : previewImage;
    if (isValid) {
      dispatch(setPersonalizationErrorFromAddToCart(false));
    }
  }, [textEntered, previewImage, imageWithText, dispatch]);

  const errorValidation = imageWithText ? !(previewImage && textEntered) : !previewImage;

  useEffect(() => {
    if (errorValidation && personalizationError) {
      const cleverTapData = {
        page_name: pageName(isHome, isPLP, pathname, currentItem?.urlIdentifier),
        pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
        validation_text: constants.THIS_IS_REQUIRED,
        validation_for: validationFor.PERSONALIZED_IMAGE,
      };

      trackEvent(cleverTapCommonEvents.validationViewed, cleverTapData, ["clevertap"]);
    }
  }, [personalizationError]);

  /**
   * Renders the appropriate personalization variant.
   * In this case, it returns a single image personalization variant.
   *
   * @returns {JSX.Element} The image personalization component.
   */
  const renderPersonlizationVariant = () => {
    if (isCustomizedImage) {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return (
        <CustomizedImagePersonalization
          urlIdentifier={urlIdentifier}
          isMobile={isMobile}
          item={item}
          productId={productId}
          setPreviewImage={setPreviewImage}
          previewImage={previewImage}
          cleverTapData={cleverTapData}
        />
      );
    } else if (item?.maxInputRequired > 1 || personalizedOptions?.length > 2) {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return (
        <MultiImagePersonalization
          item={item}
          productId={productId}
          setPreviewImage={setPreviewImage}
          previewImage={previewImage}
          isMobile={isMobile}
          imageWithText={imageWithText}
          personalizedOptions={personalizedOptions}
          setTextEntered={setTextEntered}
          textEntered={textEntered}
          cleverTapData={cleverTapData}
        />
      );
    }
    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <SingleImagePersonalization
        item={item}
        productId={productId}
        setPreviewImage={setPreviewImage}
        setImageSizeError={setImageSizeError}
        previewImage={previewImage}
        cleverTapData={cleverTapData}
      />
    );
  };

  /**
   * Renders the preview of the uploaded personalized image, or a placeholder if no image is uploaded.
   *
   * @returns {JSX.Element} The preview image or placeholder.
   */
  const renderPersonalizedImagePreview = () => {
    if (contentType === constants.AUDIO) {
      return (
        <div className="pl-16">
          <CustomImage
            src="/icons/audio.svg"
            alt="audio_image"
            width={isMobile ? 72 : 52}
            height={isMobile ? 72 : 52}
          />
        </div>
      );
    }
    return typeof previewImage === "string" ? (
      <div className="pl-16">
        <img src={previewImage} className="h-80 w-80 rounded lg:h-56 lg:w-84" alt="preview_img" />
      </div>
    ) : (
      <div className="pl-4">
        <CustomImage
          src="/icons/personalized_image.svg"
          alt="personalized_image"
          width={isMobile ? 116 : 84}
          height={isMobile ? 77 : 56}
        />
      </div>
    );
  };

  /**
   * Renders details about the file size and supported file types.
   *
   * @returns {JSX.Element} The file size and type details component.
   */
  const renderFileSizeTypeDetails = () => {
    return (
      <div className="flex w-[185px] flex-shrink-0 flex-col items-start justify-center gap-10">
        {contentType === constants.TEXT ? (
          constants.ADD_TEXT_N_PREVIEW
        ) : (
          <>
            <div className="flex h-16 items-center justify-center">
              <Typography
                Tag="p"
                classes="flex items-center text-black-100 w-max font-medium text-[12px] leading-[18px] font-inter"
              >
                {constants.FORMAT}:
                <span className="font-lining-proportional font-dlig-on ml-[4px] pr-[5px] font-inter text-[12px] font-normal leading-[20px] text-grey-400">
                  {constants.ONLY} {getSupportedFileType(allowedMediaType)}
                </span>
                {previewImage && (
                  <CustomImage src="/icons/success.svg" alt="success_icon" width={14} height={14} className="ml-2" />
                )}
              </Typography>
            </div>
            <div className="flex h-16 items-center justify-center">
              <Typography
                Tag="p"
                classes="flex items-center text-black-100 w-max  font-medium text-[12px] leading-[18px] font-inter"
              >
                {constants.FILE_SIZE}:
                <span className="font-lining-proportional font-dlig-on  ml-[4px] pr-[5px] font-inter text-[12px] font-normal leading-[20px] text-grey-400">
                  {`${minSize}${minUnit} - ${maxSize}${maxUnit}`}
                </span>
                {previewImage && (
                  <CustomImage src="/icons/success.svg" alt="success_icon" width={14} height={14} className="ml-2" />
                )}
              </Typography>
            </div>
          </>
        )}

        {imageWithText && (
          <div className="flex h-16 items-center justify-center">
            <Typography
              Tag="p"
              classes="flex items-center text-black-100 w-max  font-medium text-[12px] leading-[18px] font-inter"
            >
              {constants.SMALLCASE_TEXT}:
              <span className="font-lining-proportional font-dlig-on  ml-[4px] pr-[5px] font-inter text-[12px] font-normal leading-[20px] text-grey-400">
                {constants.ENTERED_TEXT}
              </span>
              {textEntered && <CustomImage src="/icons/success.svg" width={14} height={14} className="ml-2" />}
            </Typography>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Typography Tag="p" classes="w-full font-600 text-14 text-fnp-500 pb-12 lg:pb-[8px]">
        {constants.PERSONALIZED_HEADING}
      </Typography>
      <div
        className={`grid grid-cols-1 gap-16 rounded-lg border ${(COUNTRIES.INDIA === catalogId ? selectedPincode : selectedIntLocation?.pincode) ? "" : "pointer-events-none opacity-50"} ${
          imageSizeError || (errorValidation && personalizationError) ? "border-red" : "border-grey-200"
        }  items-center py-16 lg:grid-cols-[auto_1fr] lg:gap-[10px]`}
      >
        <div className="flex gap-[1rem]">
          {renderPersonalizedImagePreview()}
          {renderFileSizeTypeDetails()}
        </div>
        <div className="flex w-full items-center justify-center px-16">{renderPersonlizationVariant()}</div>
      </div>
      {imageSizeError && (
        <ErrorMessage
          message={constants.FILE_SIZE_TEXT}
          subMessage={`${minSize}${minUnit} - ${maxSize}${maxUnit}. ${constants.ONLY} ${getSupportedFileType(allowedMediaType)}.`}
        />
      )}

      {errorValidation && personalizationError && (
        <ErrorMessage message={constants.THIS_IS_REQUIRED} containerClasses="w-full md:w-[254px]" />
      )}

      <div className={`sm:bottom-100 lg:bottom-30 xl:bottom-30 ${toastStyles}`}>
        <Toast
          variant="success"
          isToast={!!successfullyAddedToast}
          header={constants.SAVED_SUCCESSFULLY}
          duration={3000}
          onToastChange={() => dispatch(setSuccessfullyAddedToast(false))}
        />
      </div>
    </div>
  );
};

ImagePersonalization.propTypes = {
  item: PropTypes.shape({
    allowedMediaType: PropTypes.string.isRequired,
    minSize: PropTypes.string.isRequired,
    minUnit: PropTypes.string.isRequired,
    maxSize: PropTypes.string.isRequired,
    maxUnit: PropTypes.string.isRequired,
    maxInputRequired: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
  }).isRequired,
  productId: PropTypes.string.isRequired,
  isCustomizedImage: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  imageWithText: PropTypes.bool,
  personalizedOptions: PropTypes.arrayOf(PropTypes.object),
  currentItem: PropTypes.object,
  urlIdentifier: PropTypes.string,
};

export default ImagePersonalization;
